import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * The hardware the feature pages show the app on, drawn rather than photographed: a phone, a
 * laptop and a watch, each built at its real size in points and scaled to the width it is given.
 * The screen inside is live UI in the app's dark theme, so it is sharp at any size, translated, and
 * never out of date with a screenshot.
 */

/**
 * Lays its children out at `width` × `height` and scales the whole drawing to the box's width, the
 * way a screenshot shrinks: type, hairlines and shadows keep their proportions. Pure CSS, so the
 * server render is already the right size: `tan(atan2(a, b))` is the one way CSS has to turn the
 * ratio of two lengths (the box's width over the drawing's) into the plain number `scale` takes.
 */
const Fit = ({
  width,
  height,
  className,
  style,
  children,
}: {
  width: number;
  height: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) => (
  <div
    aria-hidden
    className={cn("pointer-events-none relative select-none [container-type:inline-size]", className)}
    style={{ aspectRatio: `${width} / ${height}`, ...style }}
  >
    <div
      className="absolute top-0 left-0 origin-top-left"
      style={{ width, height, transform: `scale(tan(atan2(100cqw, ${width}px)))` }}
    >
      {children}
    </div>
  </div>
);

/** Brushed dark titanium: the light catches the top-left edge and the lower curve. */
const titanium =
  "[background:linear-gradient(155deg,#6b6e74_0%,#2c2e32_14%,#16171a_38%,#34363b_60%,#141517_82%,#55585e_100%)]";
const button =
  "absolute [background:linear-gradient(90deg,#1b1c1f,#55585e_45%,#24262a)] shadow-[0_0_0_0.5px_rgba(0,0,0,0.6)]";
/** The sheen of the glass, over everything on a screen. */
const glare =
  "pointer-events-none absolute inset-0 [background:linear-gradient(118deg,rgba(255,255,255,0.09)_0%,rgba(255,255,255,0.02)_32%,transparent_48%)]";

const StatusBar = () => (
  <div className="absolute inset-x-0 top-[15px] z-20 flex h-[37px] items-center justify-between px-[30px] text-foreground">
    <span className="w-[108px] text-center text-[17px] font-semibold tracking-[-0.02em] tabular-nums">9:41</span>
    <span className="flex w-[108px] items-center justify-center gap-[6px]">
      <svg viewBox="0 0 18 12" className="h-[12px] fill-current">
        <rect x="0" y="8" width="3" height="4" rx="1" />
        <rect x="5" y="5.5" width="3" height="6.5" rx="1" />
        <rect x="10" y="3" width="3" height="9" rx="1" />
        <rect x="15" y="0" width="3" height="12" rx="1" />
      </svg>
      <svg viewBox="0 0 16 12" className="h-[12px] fill-current">
        <path d="M8 2.4c2.3 0 4.4.9 6 2.4l1.2-1.3A10.3 10.3 0 0 0 8 .6 10.3 10.3 0 0 0 .8 3.5L2 4.8a8.6 8.6 0 0 1 6-2.4Z" />
        <path d="M8 5.9c1.4 0 2.6.5 3.6 1.4l1.2-1.3A7 7 0 0 0 8 4.1a7 7 0 0 0-4.8 1.9l1.2 1.3c1-.9 2.2-1.4 3.6-1.4Z" />
        <path d="M8 9.3c.5 0 1 .2 1.3.5L8 11.2 6.7 9.8c.3-.3.8-.5 1.3-.5Z" />
      </svg>
      <svg viewBox="0 0 28 13" className="h-[13px]">
        <rect x="0.5" y="0.5" width="24" height="12" rx="3.8" className="fill-none stroke-current opacity-40" />
        <rect x="2.5" y="2.5" width="17" height="8" rx="2.2" className="fill-current" />
        <path d="M26 4.5v4c.8-.3 1.5-1 1.5-2s-.7-1.7-1.5-2Z" className="fill-current opacity-40" />
      </svg>
    </span>
  </div>
);

/**
 * An iPhone 17 Pro in dark titanium, 402 × 874 pt of screen. `children` fill the screen under the
 * status bar; leave the top 62pt for the Dynamic Island.
 */
export const Phone = ({
  children,
  className,
  style,
  screenClassName,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  screenClassName?: string;
}) => (
  <Fit width={432} height={904} className={className} style={style}>
    <div className="relative size-full drop-shadow-[0_44px_60px_rgba(0,0,0,0.55)]">
      <span className={cn(button, "top-[176px] -left-[3px] h-[34px] w-[4px] rounded-l-[2px]")} />
      <span className={cn(button, "top-[236px] -left-[3px] h-[62px] w-[4px] rounded-l-[2px]")} />
      <span className={cn(button, "top-[312px] -left-[3px] h-[62px] w-[4px] rounded-l-[2px]")} />
      <span className={cn(button, "top-[262px] -right-[3px] h-[100px] w-[4px] rounded-r-[2px]")} />
      <span className={cn(button, "top-[540px] -right-[2px] h-[64px] w-[3px] rounded-r-[2px] opacity-80")} />
      <div
        className={cn(
          "relative size-full rounded-[76px] p-[5px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.28),inset_0_-1px_1px_rgba(0,0,0,0.5),0_0_0_1px_rgba(0,0,0,0.7)]",
          titanium,
        )}
      >
        <div className="relative size-full rounded-[71px] bg-black p-[11px] shadow-[inset_0_0_0_1.5px_#26282c]">
          <div
            className={cn(
              "dark relative size-full overflow-hidden rounded-[60px] bg-background font-sans text-[15px] text-foreground antialiased",
              screenClassName,
            )}
          >
            <StatusBar />
            {children}
            <span className="absolute top-[11px] left-1/2 z-30 h-[37px] w-[124px] -translate-x-1/2 rounded-full bg-black">
              <i className="absolute top-1/2 right-[13px] size-[11px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_35%_35%,#2c3550,#0a0c14_60%)]" />
            </span>
            <span className="absolute bottom-[9px] left-1/2 z-30 h-[5px] w-[140px] -translate-x-1/2 rounded-full bg-foreground/85" />
            <span className={glare} />
          </div>
        </div>
      </div>
    </div>
  </Fit>
);

/**
 * A 14" MacBook Pro in space black, 1140 × 716 of screen. The base is wider than the lid and the
 * floor shadow sits under it, so the laptop stands on whatever card it is drawn on.
 */
export const Laptop = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => (
  <Fit width={1280} height={812} className={className} style={style}>
    <div className="relative size-full">
      <div className="absolute top-0 left-[54px] h-[766px] w-[1172px] rounded-t-[30px] rounded-b-[6px] bg-[#0c0c0e] px-[16px] pt-[18px] pb-[32px] shadow-[inset_0_0_0_1.5px_#2e2f33,inset_0_1px_0_2px_rgba(255,255,255,0.05)]">
        <div className="dark relative size-full overflow-hidden rounded-[9px] bg-background font-sans text-[14px] text-foreground antialiased">
          {children}
          <span className="absolute top-0 left-1/2 z-30 h-[26px] w-[184px] -translate-x-1/2 rounded-b-[14px] bg-[#0c0c0e]">
            <i className="absolute top-[9px] left-1/2 size-[8px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_35%_35%,#2c3550,#06070b_65%)]" />
          </span>
          <span className={glare} />
        </div>
      </div>
      <div className="absolute top-[760px] left-0 h-[30px] w-[1280px] rounded-t-[4px] rounded-b-[30px] [background:linear-gradient(180deg,#46474c_0%,#26272a_30%,#151618_70%,#0b0b0c_100%)] shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_1px_0_rgba(0,0,0,0.6)]">
        <span className="absolute top-0 left-1/2 h-[10px] w-[220px] -translate-x-1/2 rounded-b-[14px] [background:linear-gradient(180deg,#0e0e10,#2a2b2e)]" />
      </div>
      <span className="absolute top-[786px] left-[6%] h-[26px] w-[88%] rounded-[50%] bg-black/55 blur-[14px]" />
    </div>
  </Fit>
);

/**
 * An Apple Watch in jet black on a sport loop, 208 × 258 of screen. The straps run off the drawing
 * at both ends, so a card that crops it reads as a product shot.
 */
export const Watch = ({
  children,
  className,
  style,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) => (
  <Fit width={300} height={900} className={className} style={style}>
    <div className="relative size-full drop-shadow-[0_36px_44px_rgba(0,0,0,0.6)]">
      {(["top-0 h-[330px]", "bottom-0 h-[330px]"] as const).map((place) => (
        <span
          key={place}
          className={cn(
            "absolute left-1/2 w-[196px] -translate-x-1/2 [background:linear-gradient(90deg,rgba(0,0,0,0.55),transparent_18%,transparent_82%,rgba(0,0,0,0.55)),repeating-linear-gradient(0deg,#303236_0_2px,#25272b_2px_4px)]",
            place,
          )}
        />
      ))}
      <div
        className={cn(
          "absolute top-[300px] left-[22px] h-[300px] w-[250px] rounded-[72px] p-[9px] shadow-[inset_0_1px_1px_rgba(255,255,255,0.3),inset_0_-2px_2px_rgba(0,0,0,0.55),0_0_0_1px_rgba(0,0,0,0.7)]",
          titanium,
        )}
      >
        <div className="relative size-full rounded-[63px] bg-black p-[12px] shadow-[inset_0_0_0_1.5px_#222428]">
          <div className="relative size-full overflow-hidden rounded-[51px] bg-black font-sans text-white antialiased">
            {children}
            <span className={glare} />
          </div>
        </div>
      </div>
      <span className="absolute top-[366px] left-[268px] h-[50px] w-[16px] rounded-r-[6px] [background:linear-gradient(90deg,#2a2c30,#6a6d73_40%,#2a2c30),repeating-linear-gradient(0deg,transparent_0_2px,rgba(0,0,0,0.35)_2px_3px)] [background-blend-mode:multiply] shadow-[0_0_0_0.5px_rgba(0,0,0,0.7)]" />
      <span className="absolute top-[442px] left-[270px] h-[64px] w-[6px] rounded-r-[3px] [background:linear-gradient(90deg,#1b1c1f,#55585e_45%,#24262a)]" />
    </div>
  </Fit>
);
